/**
 * ReportsQueue — admin queue of user-submitted "Report to Moderator" flags
 * on forum topics, replies, and DMs.
 *
 * Data: GET /api/admin/reports?status=open|reviewed|dismissed|actioned|all
 *       PUT /api/admin/reports/{id}  {status, resolution}
 */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Button } from '../../components/ui/button';
import { Flag, CheckCircle2, X, Ban, RefreshCw, ExternalLink, Filter, ShieldAlert } from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';
import ModerationActionModal from '../../components/admin/ModerationActionModal';

const STATUS_TABS = [
  { k: 'open',      l: 'Open'      },
  { k: 'reviewed',  l: 'Reviewed'  },
  { k: 'actioned',  l: 'Actioned'  },
  { k: 'dismissed', l: 'Dismissed' },
  { k: 'all',       l: 'All'       },
];

const REASON_LABEL = {
  spam:           'Spam',
  harassment:     'Harassment',
  hate:           'Hate speech',
  sexual:         'Sexual',
  misinformation: 'Misinformation',
  self_harm:      'Self-harm',
  other:          'Other',
};

const rel = (iso) => {
  if (!iso) return '';
  try { return formatDistanceToNow(parseISO(iso), { addSuffix: true }); }
  catch { return ''; }
};

const ReportsQueue = () => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [status, setStatus] = useState('open');
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionReport, setActionReport] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/reports?status=${status}`);
      setReports(data.reports || []);
    } catch (_) { toast.error('Could not load reports'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [status]);

  const resolve = async (id, nextStatus) => {
    const resolution = nextStatus === 'actioned'
      ? window.prompt('Optional resolution note (what action was taken?):', '')
      : '';
    if (nextStatus === 'actioned' && resolution === null) return;   // cancelled prompt
    try {
      await axios.put(`${API}/admin/reports/${id}`, { status: nextStatus, resolution });
      toast.success(`Marked as ${nextStatus}`);
      setReports((prev) => prev.filter((r) => r.id !== id));
    } catch (err) { toast.error(err?.response?.data?.detail || 'Update failed'); }
  };

  const gotoTarget = (r) => {
    if (r.topic_id) navigate(`/community/t/${r.topic_id}`);
  };

  return (
    <section className="pro-card p-4 mb-5" data-testid="reports-queue">
      <div className="flex items-start gap-2 flex-wrap mb-3">
        <div className="h-8 w-8 rounded-full bg-rose-500/15 text-rose-600 flex items-center justify-center">
          <Flag className="w-4 h-4" />
        </div>
        <div>
          <div className="eyebrow text-rose-600">Community reports</div>
          <div className="text-xs text-muted-foreground">User-submitted flags on topics, replies, and DMs.</div>
        </div>
        <div className="ml-auto inline-flex rounded-full border border-border overflow-hidden text-[10px] font-bold" data-testid="reports-tabs">
          {STATUS_TABS.map((t) => (
            <button
              key={t.k}
              type="button"
              onClick={() => setStatus(t.k)}
              data-testid={`reports-tab-${t.k}`}
              className={`px-2.5 h-8 uppercase tracking-widest transition-colors ${
                status === t.k ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t.l}
            </button>
          ))}
          <button
            type="button"
            onClick={load}
            className="px-2 h-8 text-muted-foreground hover:text-foreground border-l border-border"
            data-testid="reports-refresh"
            title="Refresh"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {reports.length === 0 && !loading && (
        <div className="text-center py-6 text-sm text-muted-foreground border border-dashed border-border rounded-xl" data-testid="reports-empty">
          <Filter className="w-5 h-5 mx-auto mb-2 opacity-50" />
          {status === 'open' ? 'No open reports. All quiet.' : `No ${status} reports.`}
        </div>
      )}

      <ul className="space-y-2">
        {reports.map((r) => (
          <li key={r.id} data-testid={`report-${r.id}`} className="rounded-xl border border-border bg-muted/30 p-3">
            <div className="flex items-start gap-2 flex-wrap">
              <span className="inline-flex items-center rounded-full border font-bold text-[10px] px-2 py-0.5 bg-rose-500/15 text-rose-700 border-rose-500/40">
                {REASON_LABEL[r.reason] || r.reason}
              </span>
              <span className="text-[11px] text-muted-foreground">
                {r.topic_id ? 'Topic' : r.reply_id ? 'Reply' : 'DM'} · reported by <b className="text-foreground">{r.reporter_name || 'someone'}</b> · {rel(r.created_at)}
              </span>
              {r.status !== 'open' && (
                <span className="ml-auto text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{r.status}</span>
              )}
            </div>

            {r.snippet && (
              <blockquote className="mt-2 border-l-2 border-primary/40 pl-2 text-xs italic text-foreground/85 line-clamp-3">
                &ldquo;{r.snippet}&rdquo;
              </blockquote>
            )}
            {r.notes && (
              <div className="mt-1 text-[11px] text-muted-foreground">
                <b>Notes:</b> {r.notes}
              </div>
            )}

            {r.status === 'open' && (
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                {r.topic_id && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => gotoTarget(r)}
                    className="rounded-full"
                    data-testid={`report-view-${r.id}`}
                  >
                    <ExternalLink className="w-3 h-3 mr-1" /> View
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={() => setActionReport(r)}
                  disabled={!r.reported_author_id}
                  className="rounded-full font-bold bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
                  data-testid={`report-take-action-${r.id}`}
                  title={r.reported_author_id ? 'Open the composite moderation action modal' : 'No offender ID on this report'}
                >
                  <ShieldAlert className="w-3 h-3 mr-1" /> Take action…
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => resolve(r.id, 'actioned')}
                  className="rounded-full font-bold"
                  data-testid={`report-action-${r.id}`}
                >
                  <Ban className="w-3 h-3 mr-1" /> Mark actioned
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => resolve(r.id, 'reviewed')}
                  className="rounded-full font-bold"
                  data-testid={`report-review-${r.id}`}
                >
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Reviewed
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => resolve(r.id, 'dismissed')}
                  className="rounded-full font-bold text-muted-foreground"
                  data-testid={`report-dismiss-${r.id}`}
                >
                  <X className="w-3 h-3 mr-1" /> Dismiss
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>

      {actionReport && (
        <ModerationActionModal
          open={!!actionReport}
          onClose={() => setActionReport(null)}
          targetUser={{
            id:           actionReport.reported_author_id,
            email:        actionReport.reported_author_email,
            display_name: actionReport.reported_author_name,
          }}
          report={{
            id:       actionReport.id,
            reason:   actionReport.reason,
            topic_id: actionReport.topic_id,
            reply_id: actionReport.reply_id,
          }}
          onDone={() => { setActionReport(null); load(); }}
        />
      )}
    </section>
  );
};

export default ReportsQueue;
